import { cx } from '@/lib/format';
import type { Technology } from '@/lib/types';
import { TechChip } from './TechChip';

const STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * The two numbers on a row read differently on purpose: `×3` is a count of
 * projects, the dots are a 1–10 level. Shown as a bare number they were
 * indistinguishable, which is what the legend above the list now spells out.
 */
export function TechRow({
  tech,
  levelLabel,
  usedIn,
  variant = 'card',
}: {
  tech: Technology;
  levelLabel: string;
  usedIn: string;
  variant?: 'card' | 'plain';
}) {
  const color = tech.color ?? 'rgb(var(--c-faint))';

  return (
    <div
      style={{ '--tech': color } as React.CSSProperties}
      className={cx(
        'group/item flex items-center gap-3.5 transition-all duration-300 ease-smooth',
        variant === 'card'
          ? 'rounded-xl border border-line bg-surface p-3.5 hover:-translate-y-0.5 hover:border-[var(--tech)] hover:shadow-[0_8px_26px_-14px_var(--tech)]'
          : 'rounded-lg px-2 py-1.5 hover:bg-elevated'
      )}
    >
      <TechChip tech={tech} size={variant === 'card' ? 'md' : 'sm'} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-medium text-ink">{tech.name}</p>
          {(tech.projectCount ?? 0) > 0 && (
            <span
              className="shrink-0 font-mono text-2xs tabular text-faint"
              title={`${tech.projectCount} ${usedIn}`}
            >
              ×{tech.projectCount}
            </span>
          )}
        </div>

        <span
          className="mt-1.5 flex gap-[3px]"
          role="img"
          aria-label={`${levelLabel} ${tech.level}/10`}
          title={`${levelLabel} ${tech.level}/10`}
        >
          {STEPS.map((step) => (
            <span
              key={step}
              className="h-1.5 w-1.5 rounded-full transition-colors duration-300"
              style={{ backgroundColor: step <= tech.level ? color : 'rgb(var(--c-line))' }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

/** Key for both readings, shown once above a list of rows. */
export function LevelLegend({
  levelLegend,
  countLegend,
}: {
  levelLegend: string;
  countLegend: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-7 gap-y-2 font-mono text-2xs text-faint">
      <span className="inline-flex items-center gap-2">
        <span className="flex gap-[3px]" aria-hidden>
          {STEPS.map((step) => (
            <i
              key={step}
              className={cx(
                'h-1.5 w-1.5 rounded-full not-italic',
                step <= 6 ? 'bg-accent' : 'bg-line'
              )}
            />
          ))}
        </span>
        {levelLegend}
      </span>
      <span>{countLegend}</span>
    </div>
  );
}
